import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';

import { cn } from '../../lib/cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon';

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-accent text-accent-fg hover:bg-accent-hover shadow-card',
  secondary: 'bg-surface text-body border border-line hover:bg-surface-2',
  ghost: 'text-muted hover:bg-surface-2 hover:text-body',
  // Destructive actions are the one place colour carries meaning rather than
  // emphasis, so this is a variant rather than a `tone` prop on primary.
  danger: 'bg-danger text-danger-fg hover:bg-danger-hover shadow-card',
  link: 'text-accent underline-offset-4 hover:underline',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 gap-1.5 px-3 text-xs',
  md: 'h-9.5 gap-2 px-4 text-sm',
  lg: 'h-11 gap-2 px-5 text-sm',
  // Square, so an icon-only button is not a wide pill with a glyph adrift in it.
  icon: 'size-9 justify-center',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /**
   * Shows a spinner and blocks the click.
   *
   * Separate from `disabled` because it means something different: disabled is
   * "you cannot do this", loading is "you already did, wait". A form that only
   * disables its submit leaves the user wondering whether the click registered.
   */
  loading?: boolean;
  /** Required when the button has no text — an icon alone has no accessible name. */
  children?: ReactNode;
}

/**
 * The button's class list, for something that must be an anchor.
 *
 * A navigation action has to be a real link — middle-click, copy-link, and the
 * fact that a screen reader announces "link" rather than "button" all depend on
 * it. Nesting an `<a>` inside a `<button>` gets the appearance and loses all
 * three, so the classes are exported instead.
 */
export function buttonClass(
  options: { variant?: ButtonVariant; size?: ButtonSize; className?: string } = {},
): string {
  const { variant = 'primary', size = 'md', className } = options;
  return cn(
    'inline-flex shrink-0 items-center rounded-control font-medium transition-colors',
    'disabled:pointer-events-none disabled:opacity-50',
    VARIANTS[variant],
    SIZES[size],
    className,
  );
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading = false, disabled, className, children, type, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      // Buttons inside a form default to `submit`, which is how a Cancel button
      // ends up saving the record. Defaulted here rather than at each call site.
      type={type ?? 'button'}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex shrink-0 items-center rounded-control font-medium transition-colors',
        'disabled:pointer-events-none disabled:opacity-50',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 className="size-3.5 animate-spin" aria-hidden /> : null}
      {children}
    </button>
  );
});

/**
 * A button that looks like part of a toolbar rather than a call to action.
 *
 * Its own component because a register's toolbar has five or six of these and
 * spelling the class list out each time is how one of them ends up a different
 * height.
 */
export const ToolbarButton = forwardRef<HTMLButtonElement, ButtonProps & { active?: boolean }>(
  function ToolbarButton({ active = false, className, ...rest }, ref) {
    return (
      <Button
        ref={ref}
        variant="secondary"
        size="sm"
        className={cn(
          'border-line bg-surface font-normal text-muted hover:text-body',
          active && 'border-accent/40 bg-accent-subtle text-accent hover:text-accent',
          className,
        )}
        {...rest}
      />
    );
  },
);
